import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { useParams } from 'react-router-dom'
import CreateJs from 'preload-js'
import wpautop from 'wpautop'
import { Context } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import RevealGrid from '@/components/RevealGrid'
import ProductItem from '@/components/ProductItem'
import Footer from '@/components/Footer'
import TextBlock from '@/components/TextBlock'
import Product from '@/pages/Product'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import actions from '@/actions'
import { insertBrAfterFirstSpace } from '@/utils/strings'
import logoAbesca from '@/assets/images/logo_abesca.jpg'
import style from './style'

const useStyles = createUseStyles(style)

const ArchiveProducts = ({
  cpt: cptFromProps,
}) => {
  const classes = useStyles()
  const [load, setLoad] = useState(false)
  const { getImageUrl, setPageAnimationReady, setHeaderInverse } = useContext(Context)
  const [products, setProducts] = useState([])
  const [isSingle, setIsSingle] = useState(false)
  const preload = useRef(new CreateJs.LoadQueue())
  const $root = useRef()
  const params = useParams()

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { allProducts, categories, currentCategory, productList, strings } = useSelector((state) => ({
    allProducts: Object.values(state.page.pages) || [],
    categories: state.page.taxonomies[cptFromProps] || [],
    currentCategory: state.page.taxonomies[cptFromProps]?.find((tax) => tax.slug === params[cptFromProps]) || {},
    mediaQueryKey: state.mediaQuery.key,
    productList: state.page.archive[params[cptFromProps]] || [],
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getProducts = useCallback((cpt, tax, term) => dispatch(actions.page.getListTerms(cpt, tax, term)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Preload Main Images: preloading only thre three first image
  ------------------------------*/
  const preloadMedia = () => {
    preload.current.on('complete', completePreload)
    products.slice(0, 3).map((item) => {
      preload.current.loadFile({ id: `img-${item.id}`, src: item.image })
      return false
    })
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    getProducts('product', cptFromProps, params[cptFromProps])
  }

  /*------------------------------
  Set Product by filtering all products by current slug category
  ------------------------------*/
  useEffect(() => {
    if (productList.length > 0) {
      const mappedProducts = allProducts.filter((p) => productList.includes(p.slug)).map((item) => ({
        id: item.id,
        title: item.title.rendered,
        denomination: item.product_denomination,
        slug: item.slug,
        link: item.link,
        image: getImageUrl(item.featured_media, 'large', item.slug, 'pages'),
      }))
      setProducts(mappedProducts)
    }
  }, [productList])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  If there are products in category complete the preload
  ------------------------------*/
  useEffect(() => {
    if (products.length > 0 && products.length < 2) {
      setIsSingle(true)
      completePreload()
    }
    if (products.length > 0 && products.length >= 2) preloadMedia()
  }, [products])

  /*------------------------------
  Set Header Logo Styles
  ------------------------------*/
  useEffect(() => {
    setHeaderInverse(true)
    return () => setHeaderInverse(false)
  }, [])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta head_json={currentCategory.yoast_head} />
    )
  }

  /*------------------------------
  Render Abesca Logo only in New Jersey Page
  ------------------------------*/
  const renderAbescaLogo = () => {
    return (
      <a
        className={classes.abescaLogo}
        href="https://www.abesca.com/"
        target="_blank"
      >
        <img src={logoAbesca} alt="Logo Abesca" />
      </a>
    )
  }

  /*------------------------------
  Render Heading
  ------------------------------*/
  const renderHeading = () => {
    return categories.length > 0 && (
      <Hero
        className={classes.hero}
        back={true}
        aboveTheFold
        viewportHeight={false}
        background="light"
        fontSize="md"
        title={insertBrAfterFirstSpace(currentCategory.name)}
        imgSize="crop"
        onComplete={() => setPageAnimationReady(true)}
        rightLink={currentCategory.id === 7 && renderAbescaLogo}
      />
    )
  }

  /*------------------------------
  Render Products
  ------------------------------*/
  const renderProducts = () => {
    return products.length > 0 && (
      <div className={classes.products}>
        <RevealGrid cols={1}>
          {products.map((product) => (
            <ProductItem
              key={product.id}
              item={product}
            />
          ))}
        </RevealGrid>
      </div>
    )
  }

  /*------------------------------
  Render Category Description
  ------------------------------*/
  const renderDescription = () => {
    return currentCategory.description && (
      <div className={`${classes.wrapper} ${classes.description}`}>
        <TextBlock
          text={wpautop(currentCategory.description)}
        />
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderHeading()}
        {renderDescription()}
        {renderProducts()}
        {renderFooter()}
      </>
    )
  }

  return !isSingle ? (
    <LocomotiveScroll
      init={load}
      className={`page pageArchiveProducts ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  ) : (
    <Product
      cpt="product"
      stub={products[0].slug}
    />
  )
}

export default ArchiveProducts
